import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';

import { getSalaAuth, isFirebaseConfigured } from '../firebase/config';
import { fetchSalaTeacherProfile, saveSalaTeacherProfile } from './salaTeacherService';
import { isSalaProfileComplete, type SalaTeacherProfile } from './types';
import { setCourseStorageOwner } from '../studio/services/courseProfileStorage';
import { setRubricStorageOwner } from '../studio/services/rubricStorage';

interface SalaAuthContextValue {
  user: User | null;
  profile: SalaTeacherProfile | null;
  loading: boolean;
  firebaseReady: boolean;
  profileComplete: boolean;
  refreshProfile: () => Promise<void>;
  saveProfile: (
    partial: Pick<SalaTeacherProfile, 'displayName' | 'subjectId' | 'subjectTag' | 'schoolName'>,
  ) => Promise<SalaTeacherProfile>;
  signInEmail: (email: string, password: string) => Promise<void>;
  registerEmail: (name: string, email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SalaAuthContext = createContext<SalaAuthContextValue | null>(null);

function syncStorageOwner(uid: string | null) {
  setCourseStorageOwner(uid);
  setRubricStorageOwner(uid);
}

export function SalaAuthProvider({ children }: { children: React.ReactNode }) {
  const firebaseReady = isFirebaseConfigured();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<SalaTeacherProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (nextUser: User | null) => {
    if (!nextUser || !firebaseReady) {
      setProfile(null);
      syncStorageOwner(null);
      return;
    }
    syncStorageOwner(nextUser.uid);
    try {
      const next = await fetchSalaTeacherProfile(nextUser.uid);
      setProfile(next);
    } catch (err) {
      console.error('[SalaAuth] No se pudo leer salaTeachers', err);
      setProfile(null);
    }
  }, [firebaseReady]);

  useEffect(() => {
    if (!firebaseReady) {
      setLoading(false);
      return;
    }
    const auth = getSalaAuth();
    // Completa Google redirect (fallback cuando el popup choca con COOP).
    void getRedirectResult(auth).catch((err) => {
      console.error('[SalaAuth] getRedirectResult failed', err);
    });
    const unsub = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      void loadProfile(nextUser).finally(() => setLoading(false));
    });
    return () => unsub();
  }, [firebaseReady, loadProfile]);

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  const saveProfile = useCallback(
    async (
      partial: Pick<SalaTeacherProfile, 'displayName' | 'subjectId' | 'subjectTag' | 'schoolName'>,
    ) => {
      if (!user) throw new Error('not_authenticated');
      const saved = await saveSalaTeacherProfile({
        uid: user.uid,
        email: user.email || '',
        displayName: partial.displayName,
        subjectId: partial.subjectId,
        subjectTag: partial.subjectTag,
        schoolName: partial.schoolName,
      });
      setProfile(saved);
      return saved;
    },
    [user],
  );

  const signInEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(getSalaAuth(), email.trim(), password);
  }, []);

  const registerEmail = useCallback(async (name: string, email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(getSalaAuth(), email.trim(), password);
    const displayName = name.trim();
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
  }, []);

  const signInGoogle = useCallback(async () => {
    const auth = getSalaAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
      // Popup bloqueado / COOP / cerrado → redirect (más fiable en Chrome).
      if (
        code.includes('popup-blocked') ||
        code.includes('popup-closed') ||
        code.includes('cancelled-popup') ||
        code.includes('network-request-failed') ||
        code.includes('internal-error')
      ) {
        await signInWithRedirect(auth, provider);
        return;
      }
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(getSalaAuth());
    setProfile(null);
    syncStorageOwner(null);
  }, []);

  const value = useMemo<SalaAuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      firebaseReady,
      profileComplete: isSalaProfileComplete(profile),
      refreshProfile,
      saveProfile,
      signInEmail,
      registerEmail,
      signInGoogle,
      signOut,
    }),
    [
      user,
      profile,
      loading,
      firebaseReady,
      refreshProfile,
      saveProfile,
      signInEmail,
      registerEmail,
      signInGoogle,
      signOut,
    ],
  );

  return <SalaAuthContext.Provider value={value}>{children}</SalaAuthContext.Provider>;
}

export function useSalaAuth(): SalaAuthContextValue {
  const ctx = useContext(SalaAuthContext);
  if (!ctx) throw new Error('useSalaAuth must be used within SalaAuthProvider');
  return ctx;
}
